import _ from 'lodash'

import {Locale} from '@/utils/constants'
import {getLocaleRestrictedPath} from '@/utils/i18n/restrictedPaths'
import {routeTo} from '@/utils/router'

export enum FaqCategoryName {
  registrationProcess = 'Registration process',
  usApplications = 'US applications',
  trademarksInGeneral = 'Trademarks in general',
  risks = 'Risks',
  costAndPricing = 'Cost and pricing',
  amazonBrandRegister = 'Amazon brand register',
  tramaTrademarks = 'Trama trademarks',
  brandProtection = 'Brand protection',
}

export type FaqCategories = Record<FaqCategoryName, FaqCategoryContent>

/*
 * `id` is the element id each category and question gets on /faq - `/faq#risks`,
 * `/faq#chance-of-opposition`. It is there so the FAQPage structured data can give every question
 * its own `url`: there is no page per question, so that url is an anchor. FaqList opens whichever
 * one a link names.
 *
 * Written out rather than derived from the translation key, so renumbering keys in the sheet
 * cannot move a link to a different question. Once published, an id is a URL: do not change it.
 */
export type FaqCategoryContent = {
  id: string
  title: string
  description: string
  imageSrc: string
  imageAlt: string
  faqs: Faq[]
}

export type Faq = {
  id: string
  question: string
  answer: FaqAnswer[]
  learnMoreLink?: Partial<Record<Locale, string>>
}

export type FaqAnswer = {
  text: string
  linkHref?: string
}

const registrationProcessFaqs = [
  {
    id: 'how-does-registration-work',
    question: 'help-center.help-registration-process-question-0',
    answer: [
      {
        text: 'help-center.help-registration-process-answer-0-0',
      },
      {
        text: 'help-center.help-registration-process-answer-0-1',
        linkHref: routeTo.verification.form(),
      },
      {
        text: 'help-center.help-registration-process-answer-0-2',
      },
      {
        text: 'help-center.help-registration-process-answer-0-3',
      },
    ],
  },
  {
    id: 'how-does-verification-work',
    question: 'help-center.help-registration-process-question-1',
    answer: [
      {
        text: 'help-center.help-registration-process-answer-1-0',
      },
      {
        text: 'help-center.help-registration-process-answer-1-1',
      },
    ],
  },
  {
    id: 'can-i-file-myself',
    question: 'help-center.help-registration-process-question-2',
    answer: [
      {
        text: 'help-center.help-registration-process-answer-2-0',
      },
      {
        text: 'help-center.help-registration-process-answer-2-1',
      },
    ],
  },
  {
    id: 'confirmation-after-filing',
    question: 'help-center.help-registration-process-question-3',
    answer: [
      {
        text: 'help-center.help-registration-process-answer-3-0',
      },
    ],
  },
  {
    id: 'how-long-does-registration-take',
    question: 'help-center.help-registration-process-question-4',
    answer: [
      {
        text: 'help-center.help-registration-process-answer-4-0',
      },
    ],
  },
  {
    id: 'is-power-of-attorney-necessary',
    question: 'help-center.help-registration-process-question-5',
    answer: [
      {
        text: 'help-center.help-registration-process-answer-5-0',
      },
    ],
  },
  {
    id: 'how-trademark-offices-evaluate-applications',
    question: 'help-center.help-registration-process-question-6',
    answer: [
      {
        text: 'help-center.help-registration-process-answer-6-0',
      },
    ],
  },
  {
    id: 'personal-or-business-name',
    question: 'help-center.help-registration-process-question-7',
    answer: [
      {
        text: 'help-center.help-registration-process-answer-7-0',
      },
    ],
    learnMoreLink: {
      [Locale.en]: routeTo.tmAcademy.article(
        'should-i-apply-for-a-trademark-in-personal-or-business-name',
      ),
      [Locale.sk]: routeTo.tmAcademy.article(
        'mam-poziadat-o-ochrannu-znamku-na-osobne-alebo-obchodne-meno',
      ),
      [Locale.cs]: routeTo.tmAcademy.article(
        'mam-pozadat-o-ochrannou-znamku-na-osobni-nebo-obchodni-jmeno',
      ),
      [Locale.es]: routeTo.tmAcademy.article(
        'debo-solicitar-una-marca-a-nombre-personal-o-a-nombre-de-la-empresa',
      ),
    },
  },
]

const usApplicationsFaqs = [
  {
    id: 'what-is-intent-to-use',
    question: 'help-center.help-us-applications-question-0',
    answer: [
      {
        text: 'help-center.help-us-applications-answer-0-0',
      },
      {
        text: 'help-center.help-us-applications-answer-0-1',
      },
      {
        text: 'help-center.help-us-applications-answer-0-2',
      },
    ],
  },
  {
    id: 'when-is-a-trademark-used-in-commerce',
    question: 'help-center.help-us-applications-question-1',
    answer: [
      {
        text: 'help-center.help-us-applications-answer-1-0',
      },
    ],
  },
  {
    id: 'intent-to-use-or-wait',
    question: 'help-center.help-us-applications-question-2',
    answer: [
      {
        text: 'help-center.help-us-applications-answer-2-0',
      },
    ],
  },
  {
    id: 'date-of-first-use-anywhere',
    question: 'help-center.help-us-applications-question-3',
    answer: [
      {
        text: 'help-center.help-us-applications-answer-3-0',
      },
    ],
  },
  {
    id: 'date-of-first-use-in-commerce',
    question: 'help-center.help-us-applications-question-4',
    answer: [
      {
        text: 'help-center.help-us-applications-answer-4-0',
      },
    ],
  },
]

const trademarksInGeneralFaqs = [
  {
    id: 'how-long-is-a-trademark-valid',
    question: 'help-center.help-trademarks-in-general-question-0',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-0-0',
      },
    ],
  },
  {
    id: 'what-are-trademark-classes',
    question: 'help-center.help-trademarks-in-general-question-1',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-1-0',
      },
      {
        text: 'help-center.help-trademarks-in-general-answer-1-1',
      },
    ],
  },
  {
    id: 'how-many-classes-to-select',
    question: 'help-center.help-trademarks-in-general-question-2',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-2-0',
      },
    ],
  },
  {
    id: 'what-is-a-logo-trademark',
    question: 'help-center.help-trademarks-in-general-question-3',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-3-0',
      },
    ],
  },
  {
    id: 'logo-colour-version',
    question: 'help-center.help-trademarks-in-general-question-4',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-4-0',
      },
    ],
  },
  {
    id: 'name-or-logo',
    question: 'help-center.help-trademarks-in-general-question-5',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-5-0',
      },
      {
        text: 'help-center.help-trademarks-in-general-answer-5-1',
      },
      {
        text: 'help-center.help-trademarks-in-general-answer-5-2',
      },
    ],
  },
  {
    id: 'trademark-eligibility-criteria',
    question: 'help-center.help-trademarks-in-general-question-6',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-6-0',
      },
      {
        text: 'help-center.help-trademarks-in-general-answer-6-1',
      },
    ],
  },
  {
    id: 'global-trademark',
    question: 'help-center.help-trademarks-in-general-question-7',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-7-0',
      },
      {
        text: 'help-center.help-trademarks-in-general-answer-7-1',
      },
    ],
  },
  {
    id: 'owning-a-trademark-as-a-natural-person',
    question: 'help-center.help-trademarks-in-general-question-8',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-8-0',
      },
    ],
  },
  {
    id: 'more-than-one-owner',
    question: 'help-center.help-trademarks-in-general-question-9',
    answer: [
      {
        text: 'help-center.help-trademarks-in-general-answer-9-0',
      },
    ],
  },
]

const risksFaqs = [
  {
    id: 'similar-trademarks-registered',
    question: 'help-center.help-risks-question-0',
    answer: [
      {
        text: 'help-center.help-risks-answer-0-0',
      },
      {
        text: 'help-center.help-risks-answer-0-1',
      },
      {
        text: 'help-center.help-risks-answer-0-2',
      },
    ],
  },
  {
    id: 'when-someone-opposes',
    question: 'help-center.help-risks-question-1',
    answer: [
      {
        text: 'help-center.help-risks-answer-1-0',
      },
    ],
  },
  {
    id: 'chance-of-opposition',
    question: 'help-center.help-risks-question-2',
    answer: [
      {
        text: 'help-center.help-risks-answer-2-0',
      },
    ],
  },
  {
    id: 'opposition-from-a-different-class',
    question: 'help-center.help-risks-question-3',
    answer: [
      {
        text: 'help-center.help-risks-answer-3-0',
      },
    ],
  },
  {
    id: 'opposition-from-the-same-class',
    question: 'help-center.help-risks-question-4',
    answer: [
      {
        text: 'help-center.help-risks-answer-4-0',
      },
    ],
  },
  {
    id: 'opposition-without-a-trademark',
    question: 'help-center.help-risks-question-5',
    answer: [
      {
        text: 'help-center.help-risks-answer-5-0',
      },
    ],
  },
  {
    id: 'challenge-after-registration',
    question: 'help-center.help-risks-question-6',
    answer: [
      {
        text: 'help-center.help-risks-answer-6-0',
      },
    ],
  },
  {
    id: 'wordmark-or-logo-success-rate',
    question: 'help-center.help-risks-question-7',
    answer: [
      {
        text: 'help-center.help-risks-answer-7-0',
      },
    ],
  },
]

const costAndPricingFaqs = [
  {
    id: 'why-is-trama-affordable',
    question: 'help-center.help-cost-and-pricing-question-0',
    answer: [
      {
        text: 'help-center.help-cost-and-pricing-answer-0-0',
      },
    ],
  },
  {
    id: 'how-much-does-it-cost',
    question: 'help-center.help-cost-and-pricing-question-1',
    answer: [
      {
        text: 'help-center.help-cost-and-pricing-answer-1-0',
        linkHref: routeTo.pricing(),
      },
    ],
  },
  {
    id: 'is-the-price-final',
    question: 'help-center.help-cost-and-pricing-question-2',
    answer: [
      {
        text: 'help-center.help-cost-and-pricing-answer-2-0',
      },
    ],
  },
  {
    id: 'payment-plan',
    question: 'help-center.help-cost-and-pricing-question-3',
    answer: [
      {
        text: 'help-center.help-cost-and-pricing-answer-3-0',
      },
    ],
  },
  {
    id: 'money-back-guarantee',
    question: 'help-center.help-cost-and-pricing-question-4',
    answer: [
      {
        text: 'help-center.help-cost-and-pricing-answer-4-0',
      },
    ],
  },
  {
    id: 'discounts',
    question: 'help-center.help-cost-and-pricing-question-5',
    answer: [
      {
        text: 'help-center.help-cost-and-pricing-answer-5-0',
      },
    ],
  },
  {
    id: 'what-is-included',
    question: 'help-center.help-cost-and-pricing-question-6',
    answer: [
      {
        text: 'help-center.help-cost-and-pricing-answer-6-0',
      },
    ],
  },
  {
    id: 'government-fees',
    question: 'help-center.help-cost-and-pricing-question-7',
    answer: [
      {
        text: 'help-center.help-cost-and-pricing-answer-7-0',
      },
    ],
  },
  {
    id: 'vat',
    question: 'help-center.help-cost-and-pricing-question-8',
    answer: [
      {
        text: 'help-center.help-cost-and-pricing-answer-8-0',
      },
    ],
  },
]

const amazonBrandRegisterFaqs = [
  {
    id: 'amazon-brand-registry-qualification',
    question: 'help-center.help-amazon-brand-register-question-0',
    answer: [
      {
        text: 'help-center.help-amazon-brand-register-answer-0-0',
      },
    ],
  },
]

const tramaTrademarksFaqs = [
  {
    id: 'jurisdictions-covered',
    question: 'help-center.help-trama-trademarks-question-0',
    answer: [
      {
        text: 'help-center.help-trama-trademarks-answer-0-0',
      },
    ],
  },
  {
    id: 'trademark-watch',
    question: 'help-center.help-trama-trademarks-question-1',
    answer: [
      {
        text: 'help-center.help-trama-trademarks-answer-1-0',
      },
    ],
  },
  {
    id: 'trademark-follow-up-agenda',
    question: 'help-center.help-trama-trademarks-question-2',
    answer: [
      {
        text: 'help-center.help-trama-trademarks-answer-2-0',
      },
    ],
  },
  {
    id: 'company-background',
    question: 'help-center.help-trama-trademarks-question-3',
    answer: [
      {
        text: 'help-center.help-trama-trademarks-answer-3-0',
      },
    ],
  },
]

const brandProtectionFaqs = [
  {
    id: 'how-does-brand-monitoring-work',
    question: 'help-center.help-brand-protection-question-0',
    answer: [
      {
        text: 'help-center.help-brand-protection-answer-0-0',
      },
      {
        text: 'help-center.help-brand-protection-answer-0-1',
      },
      {
        text: 'help-center.help-brand-protection-answer-0-2',
      },
      {
        text: 'help-center.help-brand-protection-answer-0-3',
      },
    ],
  },
  {
    id: 'monitoring-without-a-trademark',
    question: 'help-center.help-brand-protection-question-1',
    answer: [
      {
        text: 'help-center.help-brand-protection-answer-1-0',
      },
      {
        text: 'help-center.help-brand-protection-answer-1-1',
      },
    ],
  },
  {
    id: 'which-platforms-to-monitor',
    question: 'help-center.help-brand-protection-question-2',
    answer: [
      {
        text: 'help-center.help-brand-protection-answer-2-0',
      },
      {
        text: 'help-center.help-brand-protection-answer-2-1',
      },
    ],
  },
  {
    id: 'first-report',
    question: 'help-center.help-brand-protection-question-3',
    answer: [
      {
        text: 'help-center.help-brand-protection-answer-3-0',
      },
    ],
  },
  {
    id: 'when-do-i-start-paying',
    question: 'help-center.help-brand-protection-question-4',
    answer: [
      {
        text: 'help-center.help-brand-protection-answer-4-0',
      },
    ],
  },
]

export const faqCategories: FaqCategories = {
  [FaqCategoryName.registrationProcess]: {
    id: 'registration-process',
    title: 'help-center.help-registration-process-title',
    description: 'help-center.help-registration-process-desc',
    imageSrc: '/images/icons/trademark-registration.svg',
    imageAlt: 'Trademark registration',
    faqs: registrationProcessFaqs,
  },
  [FaqCategoryName.usApplications]: {
    id: 'us-applications',
    title: 'help-center.help-us-applications-title',
    description: 'help-center.help-us-applications-desc',
    imageSrc: '/images/icons/us-map.svg',
    imageAlt: 'US map',
    faqs: usApplicationsFaqs,
  },
  [FaqCategoryName.trademarksInGeneral]: {
    id: 'trademarks-in-general',
    title: 'help-center.help-trademarks-in-general-title',
    description: 'help-center.help-trademarks-in-general-desc',
    imageSrc: '/images/icons/document-with-star.svg',
    imageAlt: 'Document with a star',
    faqs: trademarksInGeneralFaqs,
  },
  [FaqCategoryName.risks]: {
    id: 'risks',
    title: 'help-center.help-risks-title',
    description: 'help-center.help-risks-desc',
    imageSrc: '/images/icons/warning.svg',
    imageAlt: 'Warning',
    faqs: risksFaqs,
  },
  [FaqCategoryName.costAndPricing]: {
    id: 'cost-and-pricing',
    title: 'help-center.help-cost-and-pricing-title',
    description: 'help-center.help-cost-and-pricing-desc',
    imageSrc: '/images/icons/cost.svg',
    imageAlt: 'Price tag',
    faqs: costAndPricingFaqs,
  },
  [FaqCategoryName.amazonBrandRegister]: {
    id: 'amazon-brand-registry',
    title: 'help-center.help-amazon-brand-register-title',
    description: 'help-center.help-amazon-brand-register-desc',
    imageSrc: '/images/icons/amazon-register.svg',
    imageAlt: 'Amazon brand register',
    faqs: amazonBrandRegisterFaqs,
  },
  [FaqCategoryName.tramaTrademarks]: {
    id: 'about-trama',
    title: 'help-center.help-trama-trademarks-title',
    description: 'help-center.help-trama-trademarks-desc',
    imageSrc: '/images/icons/ice-cream-cup.svg',
    imageAlt: 'Ice cream cup',
    faqs: tramaTrademarksFaqs,
  },
  [FaqCategoryName.brandProtection]: {
    id: 'brand-protection',
    title: 'help-center.help-brand-protection-title',
    description: 'help-center.help-brand-protection-desc',
    imageSrc: '/images/icons/binoculars.svg',
    imageAlt: 'Binoculars',
    faqs: brandProtectionFaqs,
  },
}

export const getFAQsContent = (primaryCategory?: FaqCategoryName, locale?: Locale) => {
  // Brand protection is offered only where its pages are served. Elsewhere its answers exist only in
  // English, so the category is left out rather than shown untranslated.
  const hasBrandProtection =
    !locale || getLocaleRestrictedPath(`/${locale}${routeTo.brandMonitoring.home()}`) === null

  const categoriesByLanguage = hasBrandProtection
    ? faqCategories
    : (_.omit(faqCategories, FaqCategoryName.brandProtection) as FaqCategories)

  const sortedCategories =
    primaryCategory && categoriesByLanguage[primaryCategory]
      ? ({
          [primaryCategory]: categoriesByLanguage[primaryCategory],
          ..._.omit(categoriesByLanguage, primaryCategory),
        } as FaqCategories)
      : categoriesByLanguage

  return {
    metaTitle: 'page.faq-meta-title',
    metaDescription: 'page.faq-meta-description',
    hero: {
      title: 'page.faq-hero-title',
      subtitle: 'page.faq-hero-subtitle',
    },
    categories: sortedCategories,
  }
}
